// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT ACTION MODAL — picker RACI (lot B.9 — découpage du monstre)
// Migration S4.1 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Dropdown user picker pour les champs RACI (R/A/C/I) de la modale d'édition
// d'action. Ouvert au clic sur le bouton "+" d'un champ RACI, ancré sous le
// bouton, recherche + multi-sélection par checkboxes.
//
// Historique : auparavant via openModal() qui appelait closeModal() → la
// modale d'édition d'action était détruite avant que le picker s'ouvre.
// Remplacé par un dropdown ancré (z-index 1000 > modal-overlay 200).
//
// Bare refs utilisées (résolues via core-globals.d.ts) :
//   esc, t, trigramHtml, userLabel

interface RaciPickableUser {
  id: number;
  trigram?: string | null;
  name: string;
  is_team?: boolean;
}

export function _getRaciIds(field: string): number[] {
  const block = document.getElementById(`raci-${field}`);
  if (!block) return [];
  return Array.from(block.querySelectorAll<HTMLElement>('.raci-chip[data-uid]')).map((c) =>
    parseInt(c.dataset['uid'] || '0'),
  );
}

// User picker pour les champs RACI — dropdown inline ancré sous le
// bouton "+" cliqué.
//
// Comportement :
//  - Affichage en position fixed sous le bouton, z-index > modal (200).
//  - Champ de recherche (filtre nom/trigram) en haut, autofocus.
//  - Liste scrollable (max-height ~380px) — tient pour 100+ users.
//  - Multi-sélection par checkbox, état conservé en mémoire via Set
//    pour survivre aux re-render lors de la recherche.
//  - Validation : clic-extérieur ou touche Escape → applique les
//    changements aux chips de la modal d'édition et ferme.
//  - Un seul dropdown ouvert à la fois (les anciens sont retirés).
export function showRaciPicker(
  fieldId: string,
  anchorBtn: HTMLElement,
  users: ReadonlyArray<RaciPickableUser>,
): void {
  // Ferme tout dropdown picker précédemment ouvert (au cas où on clique
  // sur "+" d'un autre champ sans avoir fermé le précédent).
  document.querySelectorAll('.raci-picker-dropdown').forEach((el) => el.remove());

  // Filtre actifs (cf. user 2026-05-21) : on ne propose pas un user inactif
  // / parti dans le picker RACI. Si la valeur existante référence un user
  // inactif, son chip reste affiché (déjà saisi) mais on ne peut pas le
  // re-sélectionner — c'est cohérent : on n'ajoute plus d'inactifs.
  const userPool: ReadonlyArray<RaciPickableUser> = (Array.isArray(users) ? users : []).filter(
    (u) => isUserActive(u as never),
  );

  const chipsEl = document.getElementById('chips-' + fieldId);
  const currentIds = Array.from(
    chipsEl?.querySelectorAll<HTMLElement>('.raci-chip[data-uid]') || [],
  ).map((el) => parseInt(el.dataset['uid'] || '0'));

  let search = '';
  const selectedIds = new Set<number>(currentIds);

  const renderList = (): string => {
    const q = search.toLowerCase();
    const filtered = userPool.filter(
      (u) => !q || u.name.toLowerCase().includes(q) || (u.trigram || '').toLowerCase().includes(q),
    );
    if (filtered.length === 0) {
      return `<div style="color:var(--text3);font-size:12px;padding:12px;text-align:center">${esc(t('actionModal.picker.empty'))}</div>`;
    }
    return filtered
      .map((u) => {
        const sel = selectedIds.has(u.id);
        return `<label class="raci-pick-row" data-uid="${u.id}"
        style="display:flex;align-items:center;gap:7px;padding:6px 10px;cursor:pointer;font-size:12px;${sel ? 'background:var(--accent-bg);' : ''}">
        <input type="checkbox" class="raci-pick-chk" value="${u.id}" ${sel ? 'checked' : ''} style="margin:0">
        ${trigramHtml(u, { extraStyle: 'font-size:10px;color:var(--text3);min-width:32px;display:inline-block', placeholder: '' })}
        <span>${esc(u.name)}</span>
      </label>`;
      })
      .join('');
  };

  const dropdown = document.createElement('div');
  dropdown.className = 'raci-picker-dropdown';
  dropdown.style.cssText = [
    'position:fixed',
    'z-index:1000', // au-dessus de modal-overlay (200)
    'background:var(--bg2)',
    'border:1px solid var(--border2)',
    'border-radius:var(--radius)',
    'box-shadow:0 8px 24px rgba(0,0,0,.15)',
    'width:300px',
    'max-height:380px',
    'display:flex',
    'flex-direction:column',
    'overflow:hidden',
  ].join(';');
  dropdown.innerHTML = `
    <div style="padding:8px;border-bottom:1px solid var(--border)">
      <input type="text" class="raci-picker-search" placeholder="${esc(t('actionModal.picker.searchPlaceholder'))}"
        autocomplete="off"
        style="width:100%;font-size:12px;padding:6px 10px;border:1px solid var(--border2);border-radius:var(--radius)">
    </div>
    <div class="raci-picker-list" style="flex:1;overflow-y:auto">${renderList()}</div>
    <div style="padding:8px 10px;border-top:1px solid var(--border);display:flex;justify-content:flex-end">
      <button class="raci-picker-add btn btn-primary btn-sm" type="button">${esc(t('actionModal.picker.btnAdd'))}</button>
    </div>`;

  document.body.appendChild(dropdown);

  // Positionnement : sous le bouton, calé à droite si pas la place à gauche.
  const rect = anchorBtn.getBoundingClientRect();
  const ddWidth = 300;
  const margin = 8;
  let left = rect.right - ddWidth;
  if (left < margin) left = rect.left;
  if (left + ddWidth > window.innerWidth - margin) {
    left = window.innerWidth - ddWidth - margin;
  }
  let top = rect.bottom + 4;
  // Si pas assez de place en bas, on l'ouvre au-dessus.
  if (top + 380 > window.innerHeight - margin) {
    top = Math.max(margin, rect.top - 380 - 4);
  }
  dropdown.style.top = top + 'px';
  dropdown.style.left = left + 'px';

  const listEl = dropdown.querySelector<HTMLElement>('.raci-picker-list');
  const searchInput = dropdown.querySelector<HTMLInputElement>('.raci-picker-search');
  if (!listEl || !searchInput) return;

  searchInput.addEventListener('input', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    search = target?.value ?? '';
    listEl.innerHTML = renderList();
  });

  // Mise à jour immédiate du Set + du visuel à chaque toggle de checkbox.
  listEl.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLInputElement | null;
    if (!target || !target.classList.contains('raci-pick-chk')) return;
    const id = parseInt(target.value);
    if (target.checked) {
      selectedIds.add(id);
    } else {
      selectedIds.delete(id);
    }
    const row = target.closest<HTMLElement>('.raci-pick-row');
    if (row) row.style.background = target.checked ? 'var(--accent-bg)' : '';
  });

  setTimeout(() => searchInput.focus(), 0);

  // Bouton "Ajouter" : confirme la sélection et ferme. Plus explicite que
  // de fermer en cliquant ailleurs ou en tapant ESC (le user trouvait ça
  // troublant — comportement caché). On garde clic-outside et ESC comme
  // raccourcis de fermeture.
  const addBtn = dropdown.querySelector<HTMLButtonElement>('.raci-picker-add');
  if (addBtn) {
    addBtn.addEventListener('click', (ev: Event) => {
      ev.stopPropagation();
      applyAndClose();
    });
  }

  // Application + fermeture.
  const applyAndClose = (): void => {
    document.removeEventListener('mousedown', closeOnOutsideClick, true);
    document.removeEventListener('keydown', closeOnEscape, true);
    if (chipsEl) {
      const selectedUsers = Array.from(selectedIds)
        .map((id) => userPool.find((u) => u.id === id))
        .filter((u): u is RaciPickableUser => Boolean(u));
      const chips =
        selectedUsers.length > 0
          ? selectedUsers
              .map(
                (u) => `<span class="raci-chip" data-uid="${u.id}" data-field="${fieldId}"
          title="${esc(u.name)}"
          style="display:inline-flex;align-items:center;gap:3px;background:var(--accent-bg);color:var(--accent);
                 font-size:11px;padding:2px 7px;border-radius:10px;margin:2px;cursor:default">
          ${esc(userLabel(u, true))}
          <button data-remove-raci="${u.id}" data-field="${fieldId}"
            style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:13px;padding:0 2px;line-height:1">×</button>
        </span>`,
              )
              .join('')
          : `<span class="raci-empty" style="color:var(--text3);font-size:11px">${esc(t('actionModal.raci.empty'))}</span>`;
      // eslint-disable-next-line no-restricted-syntax -- chips construit ci-dessus avec esc() sur userLabel().
      chipsEl.innerHTML = chips;
      // Réattache les listeners "remove" sur les nouvelles puces.
      chipsEl.querySelectorAll<HTMLButtonElement>('[data-remove-raci]').forEach((btn) => {
        btn.addEventListener('click', () =>
          _removeRaciChip(btn.dataset['field'] || '', parseInt(btn.dataset['removeRaci'] || '0')),
        );
      });
    }
    dropdown.remove();
  };

  const closeOnOutsideClick = (e: MouseEvent): void => {
    const target = e.target as Node | null;
    if (!target) return;
    // Ignore les clics dans le dropdown ou sur le bouton qui l'a ouvert.
    if (dropdown.contains(target) || target === anchorBtn || anchorBtn.contains(target)) {
      return;
    }
    applyAndClose();
  };
  const closeOnEscape = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation(); // évite que la modal d'édition se ferme aussi
      applyAndClose();
    }
  };

  // Délai pour ne pas attraper l'événement mousedown du clic
  // d'ouverture lui-même.
  setTimeout(() => {
    document.addEventListener('mousedown', closeOnOutsideClick, true);
    document.addEventListener('keydown', closeOnEscape, true);
  }, 0);
}

export function _addRaciChip(field: string, u: RaciPickableUser): void {
  const chipsEl = document.getElementById(`chips-${field}`);
  if (!chipsEl) return;
  if (chipsEl.querySelector(`.raci-chip[data-uid="${u.id}"]`)) return;
  chipsEl.querySelector('.raci-empty')?.remove();
  const chip = document.createElement('span');
  chip.className = 'raci-chip';
  chip.dataset['uid'] = String(u.id);
  chip.dataset['field'] = field;
  chip.style.cssText =
    'display:inline-flex;align-items:center;gap:3px;background:var(--accent-bg);color:var(--accent);font-size:11px;padding:2px 7px;border-radius:10px;margin:2px';
  chip.title = u.name;
  // Libellé isolé dans un `<span>` : la fiche réunion souligne CE span (score
  // de pertinence de l'invité), sans décorer le « × » de suppression.
  chip.innerHTML = `<span class="raci-chip-label">${esc(userLabel(u, true))}</span><button data-remove-raci="${u.id}" data-field="${field}" style="background:none;border:none;cursor:pointer;color:var(--accent);font-size:13px;padding:0 2px;line-height:1">×</button>`;
  chipsEl.appendChild(chip);
  chip
    .querySelector<HTMLButtonElement>('[data-remove-raci]')
    ?.addEventListener('click', () => _removeRaciChip(field, u.id));
}

export function _removeRaciChip(field: string, uid: number): void {
  const chipsEl = document.getElementById(`chips-${field}`);
  if (!chipsEl) return;
  chipsEl.querySelector(`.raci-chip[data-uid="${uid}"]`)?.remove();
  if (!chipsEl.querySelector('.raci-chip'))
    chipsEl.innerHTML = `<span class="raci-empty" style="color:var(--text3);font-size:11px">${esc(t('actionModal.raci.empty'))}</span>`;
}
